import React from 'react';

import { Select } from 'components/ui/select';

import RemovableBadge from './removable_badge';

import type { OupData } from 'components/users/types';

export interface OfficeOption {
  value: number;
  label: string;
}

interface Props {
  inputId: string;
  options: OfficeOption[];
  selectedIds: number[];
  placeholder?: string;
  // Only for a field with no visible Label pointing at inputId.
  ariaLabel?: string;
  onChange: (ids: number[]) => void;
}

// An office that was deleted or disabled after it was chosen.
export const UNKNOWN_OFFICE = 'Unknown office';

export const officeOptionsOf = (officesById: OupData['officesById']): OfficeOption[] => (
  Object.entries(officesById).map(([id, office]) => ({ value: Number(id), label: office.name }))
);

export default function OfficesField({
  inputId, options, selectedIds, placeholder = 'Add an office', ariaLabel, onChange,
}: Props) {
  const labelById = new Map(options.map(option => [option.value, option.label]));

  return (
    <div>
      {selectedIds.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {selectedIds.map(id => (
            <RemovableBadge
              data-testid="selected-office"
              key={id}
              label={labelById.get(id) ?? UNKNOWN_OFFICE}
              onRemove={() => onChange(selectedIds.filter(selected => selected !== id))}
            />
          ))}
        </div>
      )}
      <div style={{ maxWidth: '22rem' }}>
        <Select<OfficeOption>
          aria-label={ariaLabel}
          getOptionLabel={option => option.label}
          getOptionValue={option => String(option.value)}
          inputId={inputId}
          options={options.filter(option => !selectedIds.includes(option.value))}
          placeholder={placeholder}
          value={null}
          onChange={option => option && onChange([...selectedIds, option.value])}
        />
      </div>
    </div>
  );
}
